import { useTranslation } from "@msuite/ui/localization";
import { Panels } from "@msuite/ui/panels";
import { SegmentedControl } from "@msuite/ui/segmented-control";
import { Tabs } from "@msuite/ui/tabs";
import { useState } from "react";
import { useCitadel } from "@/hooks/use-citadel";
import type { UserPanelBaseProps } from "../types";
import type { UserSettingsTab } from "./shared";
import { UserPermissions } from "./user-permissions";
import { UserRoles } from "./user-roles";
import { UserSettingsGeneral } from "./user-settings-general";

export function UserSettings({ userId }: UserPanelBaseProps) {
	/** State */
	const [activeTab, setActiveTab] = useState<UserSettingsTab>("general");

	/** Hooks */
	const { t } = useTranslation();
	const { citadel } = useCitadel();

	/** Computed */
	// Both panels behind this segment read `manage Permission`-gated endpoints — a colleague's
	// grants and the organisation's role catalogue. Without the rule the segment would open two
	// empty tables and two refusals, so it is not offered; `general` is the only value the
	// control can then hold, which is what keeps the guard below unreachable rather than
	// decorative.
	const canManagePermissions = citadel?.can("manage", "Permission") === true;

	/** Render */
	return (
		<Panels>
			<Panels.Panel flex={1}>
				<SegmentedControl
					value={activeTab}
					orientation="vertical"
					onChange={setActiveTab}
					items={[
						{
							value: "general",
							label: t("general.general"),
							cyId: "user-settings-general-tab",
						},
						...(canManagePermissions
							? [
									{
										value: "permissions" as const,
										label: t("permissions.permissions"),
										cyId: "user-settings-permissions-tab",
									},
								]
							: []),
					]}
				/>
			</Panels.Panel>
			<Panels.Panel
				flex={3}
				className="bottom-space"
			>
				{activeTab === "general" && <UserSettingsGeneral userId={userId} />}
				{/* Roles and permissions under two tabs rather than one long scroll — the same
				    split the role detail panel carries, seen from the person's side. The wrapper
				    is the role panel's height chain: without it the tab panel is a plain block,
				    the grid's height resolves to zero, and the virtualised rows clip away. The
				    x-axis is clipped for the role panel's reason: the tabs bleed eight pixels
				    into either gutter, and an open axis answers that bleed with a scrollbar. */}
				{activeTab === "permissions" && canManagePermissions && (
					<div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden v-stack">
						<Tabs
							panelKey="user-permissions-tabs"
							groups={[
								{
									name: "permissions",
									panels: [
										{
											label: t("permissions.permissions"),
											panel: () => <UserPermissions userId={userId} />,
											errors: null,
											cyId: "user-permissions-tab",
										},
										{
											label: t("roles.roles"),
											panel: () => <UserRoles userId={userId} />,
											errors: null,
											cyId: "user-roles-tab",
										},
									],
								},
							]}
						/>
					</div>
				)}
			</Panels.Panel>
		</Panels>
	);
}
